import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ordersApi, refundsApi, returnsApi } from "./orders-api";
import type { OrdersFilter } from "./types";

const KEY_ORDERS = ["orders"];
const KEY_RETURNS = ["returns"];
const KEY_REFUNDS = ["refunds"];

export function useOrdersList(filter: OrdersFilter, enabled = true) {
	return useQuery({
		queryKey: [...KEY_ORDERS, "list", filter],
		queryFn: () => ordersApi.list(filter),
		enabled,
		placeholderData: (prev) => prev,
	});
}

export function useOrder(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_ORDERS, "detail", id],
		queryFn: () => ordersApi.get(id!),
		enabled: !!id,
	});
}

export function useOrdersRealtime() {
	return useQuery({
		queryKey: [...KEY_ORDERS, "realtime"],
		queryFn: () => ordersApi.realtimeStats(),
		refetchInterval: 30_000,
	});
}

export function useOrdersAnalytics() {
	return useQuery({
		queryKey: [...KEY_ORDERS, "analytics"],
		queryFn: () => ordersApi.analyticsStats(),
	});
}

export function useOrderEvents(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_ORDERS, "events", id],
		queryFn: () => ordersApi.listEvents(id!),
		enabled: !!id,
	});
}

export function useOrderNotes(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_ORDERS, "notes", id],
		queryFn: () => ordersApi.listNotes(id!),
		enabled: !!id,
	});
}

export function useSetOrderStatus(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			status,
			reasonCode,
		}: {
			status: string;
			reasonCode?: string;
		}) => ordersApi.setStatus(id, status, reasonCode),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: KEY_ORDERS });
		},
	});
}

export function useCancelOrder(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			reasonCode,
			forceApprovalSubmitted,
		}: {
			reasonCode: string;
			forceApprovalSubmitted?: boolean;
		}) => ordersApi.cancel(id, reasonCode, forceApprovalSubmitted),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

/** Archive / désarchive / supprime (douce) — l'id est passé au moment de l'appel (liste). */
export function useArchiveOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => ordersApi.archive(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useUnarchiveOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => ordersApi.unarchive(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useDeleteOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => ordersApi.remove(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useAddOrderLine(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (line: {
			variantId: string;
			quantity: number;
			unitPriceOverride?: number;
			discountAmount?: number;
		}) => ordersApi.addLine(id, line),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useUpdateOrderLine(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			lineId,
			...patch
		}: {
			lineId: string;
			quantity?: number;
			unitPrice?: number;
			discountAmount?: number;
		}) => ordersApi.updateLine(id, lineId, patch),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useRemoveOrderLine(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (lineId: string) => ordersApi.removeLine(id, lineId),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useApplyPromo(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ code, amount }: { code: string; amount: number }) =>
			ordersApi.applyPromo(id, code, amount),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useAddOrderNote(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: string) => ordersApi.addNote(id, body),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: [...KEY_ORDERS, "notes", id] }),
	});
}

export function useCreateManualOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: Record<string, unknown>) =>
			ordersApi.createManual(payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

// ---------- VIREMENTS BANCAIRES ----------

/**
 * File des virements attendus, les échéances les plus proches d'abord.
 *
 * Rafraîchie régulièrement : le stock des commandes échues est libéré par un
 * balayage côté serveur, une liste figée ferait donc agir sur des réservations
 * qui n'existent plus.
 */
export function useBankTransfersPending(enabled = true) {
	return useQuery({
		queryKey: [...KEY_ORDERS, "bank-transfers", "pending"],
		queryFn: () => ordersApi.bankTransfersPending(),
		enabled,
		refetchInterval: 60_000,
	});
}

export function useConfirmBankTransfer(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: {
			amount: number;
			valueDate?: string;
			reference?: string;
			acceptGap?: boolean;
		}) => ordersApi.confirmBankTransfer(id, payload),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useExtendReservation(id: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ days, reason }: { days: number; reason: string }) =>
			ordersApi.extendReservation(id, days, reason),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useReleaseExpiredReservations() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => ordersApi.releaseExpiredReservations(),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

export function useBulkStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			ids,
			status,
			reasonCode,
		}: {
			ids: string[];
			status: string;
			reasonCode?: string;
		}) => ordersApi.bulkStatus(ids, status, reasonCode),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_ORDERS }),
	});
}

// ---------- RETURNS ----------

export function useReturnsList(status?: string) {
	return useQuery({
		queryKey: [...KEY_RETURNS, "list", status],
		queryFn: () => returnsApi.list(status),
	});
}

export function useReturn(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_RETURNS, "detail", id],
		queryFn: () => returnsApi.get(id!),
		enabled: !!id,
	});
}

export function useReturnAction(id: string) {
	const qc = useQueryClient();
	return {
		accept: useMutation({
			mutationFn: () => returnsApi.accept(id),
			onSuccess: () => qc.invalidateQueries({ queryKey: KEY_RETURNS }),
		}),
		reject: useMutation({
			mutationFn: (reason?: string) => returnsApi.reject(id, reason),
			onSuccess: () => qc.invalidateQueries({ queryKey: KEY_RETURNS }),
		}),
		receive: useMutation({
			mutationFn: () => returnsApi.receive(id),
			onSuccess: () => qc.invalidateQueries({ queryKey: KEY_RETURNS }),
		}),
		inspect: useMutation({
			mutationFn: (payload: {
				decision: "FULL_REFUND" | "PARTIAL_REFUND" | "REFUSED";
				inspectionNote?: string;
				partialAmount?: number;
			}) => returnsApi.inspect(id, payload),
			onSuccess: () => qc.invalidateQueries({ queryKey: KEY_RETURNS }),
		}),
	};
}

export function useCreateReturn(orderId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: {
			reasonCode: string;
			customerNote?: string;
			lines: Array<{ orderLineId: string; quantity: number }>;
		}) => returnsApi.create(orderId, payload),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: KEY_RETURNS });
			qc.invalidateQueries({ queryKey: KEY_ORDERS });
		},
	});
}

// ---------- REFUNDS ----------

export function useRefundsList(orderId?: string) {
	return useQuery({
		queryKey: [...KEY_REFUNDS, "list", orderId],
		queryFn: () => refundsApi.list(orderId),
	});
}

export function useCreateRefund(orderId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			amount,
			returnId,
			reason,
		}: {
			amount: number;
			returnId?: string;
			reason?: string;
		}) => refundsApi.create(orderId, amount, returnId, reason),
		onSuccess: () => qc.invalidateQueries({ queryKey: KEY_REFUNDS }),
	});
}
